import env from "@shared/api/env";

export type FlowFSItem = {
  name: string;
  path: string; // absolute
  type: "file" | "dir";
  size?: number;
  modified?: string; // ISO
  contentType?: string;
  version?: string | number;
};

export type ListResponse = { items: FlowFSItem[]; cursor?: string };

const withTimeout = (ms: number, signal?: AbortSignal) => {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  const proxy = new AbortController();
  const onAbort = () => proxy.abort();
  ctrl.signal.addEventListener("abort", onAbort);
  if (signal) signal.addEventListener("abort", onAbort);
  return { signal: proxy.signal, done: () => clearTimeout(t) };
};

const BASE = (env.CONDUIT_BASE || env.BLAST_API).replace(/\/$/, "");

const safeAbs = (p: string) => {
  if (!p || !p.startsWith("/")) throw new Error("PATH_NOT_ABSOLUTE");
  if (p.includes("..")) throw new Error("PATH_TRAVERSAL");
  return p;
};

const q = (o: Record<string, any>) =>
  Object.entries(o)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join("&");

const normErr = async (res: Response) => {
  let body: any = null;
  try {
    body = await res.json();
  } catch {}
  const message = body?.error?.message || res.statusText || "Request failed";
  const code = body?.error?.code || String(res.status);
  const err: any = new Error(message);
  err.status = res.status;
  err.code = code;
  err.details = body?.error?.details;
  return err;
};

const commonHeaders = (tenantId: string) => ({
  "X-Workspace-ID": tenantId,
});

export async function listFlowFS(params: {
  tenantId: string;
  flowId: string;
  prefix: string;
  recursive?: boolean;
  limit?: number;
  cursor?: string;
}): Promise<ListResponse> {
  const { tenantId, flowId, prefix, recursive, limit, cursor } = params;
  const url = `${BASE}/flowfs/list?${q({
    tenantId,
    flowId,
    prefix: safeAbs(prefix),
    recursive: recursive ? 1 : 0,
    limit,
    cursor,
  })}`;
  const { signal, done } = withTimeout(8000);
  const res = await fetch(url, { headers: commonHeaders(tenantId), signal });
  done();
  if (!res.ok) throw await normErr(res);
  const json = await res.json();
  const raw = (json.items ?? json.data ?? json.entries ?? []) as any[];
  const items: FlowFSItem[] = raw.map((e) => {
    const p: string = e.path || e.key || e.name || "";
    const type: "file" | "dir" = e.type || (p.endsWith("/") ? "dir" : "file");
    const name = p.split("/").filter(Boolean).pop() || p;
    const modified = e.modified || (typeof e.mtimeMs === "number" ? new Date(e.mtimeMs).toISOString() : undefined);
    return {
      name,
      path: p,
      type,
      size: e.size,
      modified,
      contentType: e.contentType,
      version: e.version,
    } as FlowFSItem;
  });
  return { items, cursor: json.cursor ?? undefined };
}

export async function statFlowFS(params: { tenantId: string; flowId: string; path: string }) {
  const { tenantId, flowId, path } = params;
  const url = `${BASE}/flowfs/stat?${q({ tenantId, flowId, path: safeAbs(path) })}`;
  const { signal, done } = withTimeout(8000);
  const res = await fetch(url, { headers: commonHeaders(tenantId), signal });
  done();
  if (!res.ok) throw await normErr(res);
  const etag = res.headers.get("ETag") || undefined;
  const json = await res.json();
  return { meta: json, etag };
}

export async function readFlowFS(params: {
  tenantId: string;
  flowId: string;
  path: string;
  as: "json" | "text";
  version?: string | number;
}) {
  const { tenantId, flowId, path, as, version } = params;
  const url = `${BASE}/flowfs/read?${q({ tenantId, flowId, path: safeAbs(path), as, version })}`;
  const { signal, done } = withTimeout(10000);
  const res = await fetch(url, { headers: commonHeaders(tenantId), signal });
  done();
  if (!res.ok) throw await normErr(res);
  const etag = res.headers.get("ETag") || undefined;
  const json = await res.json();
  return { ...json, etag };
}

export async function downloadFlowFS(params: {
  tenantId: string;
  flowId: string;
  path: string;
  version?: string | number;
}) {
  const { tenantId, flowId, path, version } = params;
  const url = `${BASE}/flowfs/download?${q({ tenantId, flowId, path: safeAbs(path), version })}`;
  const { signal, done } = withTimeout(120000);
  const res = await fetch(url, { headers: commonHeaders(tenantId), signal });
  done();
  if (!res.ok) throw await normErr(res);
  const blob = await res.blob();
  return {
    url: URL.createObjectURL(blob),
    contentType: res.headers.get("Content-Type") || "",
    etag: res.headers.get("ETag") || undefined,
  };
}

export async function writeJsonFlowFS(params: {
  tenantId: string;
  flowId: string;
  path: string;
  data: any;
  as?: "json" | "text";
  contentType?: string;
  ttlMs?: number;
  sensitive?: boolean;
  etag?: string;
  createOnly?: boolean;
}) {
  const { tenantId, flowId, path, data, as = "json", contentType, ttlMs, sensitive, etag, createOnly } = params;
  const url = `${BASE}/flowfs/write?${q({ tenantId, flowId })}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...commonHeaders(tenantId),
    ...(etag ? { "If-Match": etag } : {}),
    ...(createOnly ? { "If-None-Match": "*" } : {}),
  };
  const body = JSON.stringify({ path: safeAbs(path), data, as, contentType, ttlMs, sensitive });
  const res = await fetch(url, { method: "PUT", headers, body });
  if (res.status === 412) {
    const err: any = new Error("ETAG_CONFLICT");
    err.code = "412";
    err.status = 412;
    throw err;
  }
  if (!res.ok) throw await normErr(res);
  return { etag: res.headers.get("ETag") || undefined };
}

export async function writeBytesFlowFS(params: {
  tenantId: string;
  flowId: string;
  path: string;
  bytes: ArrayBuffer | Uint8Array | Blob;
  contentType: string;
  etag?: string;
  createOnly?: boolean;
}) {
  const { tenantId, flowId, path, bytes, contentType, etag, createOnly } = params;
  const url = `${BASE}/flowfs/write?${q({ tenantId, flowId, path: safeAbs(path), contentType })}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/octet-stream",
    ...commonHeaders(tenantId),
    ...(etag ? { "If-Match": etag } : {}),
    ...(createOnly ? { "If-None-Match": "*" } : {}),
  };
  const body = bytes instanceof Blob ? bytes : new Blob([bytes], { type: contentType || "application/octet-stream" });
  const res = await fetch(url, { method: "PUT", headers, body });
  if (res.status === 412) {
    const err: any = new Error("ETAG_CONFLICT");
    err.code = "412";
    err.status = 412;
    throw err;
  }
  if (!res.ok) throw await normErr(res);
  return { etag: res.headers.get("ETag") || undefined };
}

export async function moveFlowFS(params: {
  tenantId: string;
  flowId: string;
  src: string;
  dst: string;
  overwrite: boolean;
  etag?: string;
}) {
  const { tenantId, flowId, src, dst, overwrite, etag } = params;
  const url = `${BASE}/flowfs/move`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...commonHeaders(tenantId),
    ...(etag ? { "If-Match": etag } : {}),
  };
  const res = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify({ tenantId, flowId, src: safeAbs(src), dst: safeAbs(dst), overwrite }),
  });
  if (res.status === 409) {
    const e: any = await normErr(res);
    e.code = "409_CONFLICT";
    throw e;
  }
  if (res.status === 412) {
    const e: any = await normErr(res);
    e.code = "412_PRECONDITION";
    throw e;
  }
  if (!res.ok) throw await normErr(res);
  return { etag: res.headers.get("ETag") || undefined };
}

export async function deleteFlowFS(params: {
  tenantId: string;
  flowId: string;
  path: string;
  recursive?: boolean;
  etag?: string;
}) {
  const { tenantId, flowId, path, recursive, etag } = params;
  const url = `${BASE}/flowfs?${q({ tenantId, flowId, path: safeAbs(path), recursive: recursive ? 1 : 0 })}`;
  const res = await fetch(url, {
    method: "DELETE",
    headers: { ...commonHeaders(tenantId), ...(etag ? { "If-Match": etag } : {}) },
  });
  if (res.status === 404) {
    const e: any = await normErr(res);
    e.code = "404_NOT_FOUND";
    throw e;
  }
  if (res.status === 412) {
    const e: any = await normErr(res);
    e.code = "412_PRECONDITION";
    throw e;
  }
  if (!res.ok) throw await normErr(res);
  return true;
}

export const isAbsoluteSafe = (p: string) => p.startsWith("/") && !p.includes("..");
